import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { ShieldCheck, Hammer, Search, AlertCircle, Plus, Check, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const WORK_TYPES = ['Concrete', 'Iron', 'Gutter', 'Painter', 'Other'];

/**
 * Per-appraisal Quality Signoff card.
 *
 * Each (appraisal, contractor, work_type) row needs TWO signatures:
 *   1. Production Supervisor — confirms work complete
 *   2. Quality Inspector    — confirms quality acceptable
 * Both required to approve. Approval increments the contractor's jobs
 * counter (which may trigger an approval_flag for milestones).
 *
 * Operator can also set a per-job `effective_uplift_percent` that will
 * be used by the Contractor Sheet PDF instead of the contractor-wide
 * value when approved.
 */
export default function QualitySignoffCard({ appraisalId }) {
  const { api } = useAuth();
  const [signoffs, setSignoffs] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newRow, setNewRow] = useState({ contractor_id: '', work_type: 'Concrete', effective_uplift_percent: '' });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/job-signoffs?appraisal_id=${appraisalId}`);
      setSignoffs(Array.isArray(data) ? data : []);
    } catch { setSignoffs([]); }
  }, [api, appraisalId]);

  const loadContractors = useCallback(async () => {
    try { const { data } = await api.get('/contractors'); setContractors(Array.isArray(data) ? data : []); }
    catch { setContractors([]); }
  }, [api]);

  useEffect(() => { load(); loadContractors(); }, [load, loadContractors]);

  const addSignoff = async () => {
    if (!newRow.contractor_id) { toast.error('Pick a contractor'); return; }
    try {
      const payload = {
        appraisal_id: appraisalId,
        contractor_id: newRow.contractor_id,
        work_type: newRow.work_type,
        effective_uplift_percent: newRow.effective_uplift_percent === '' ? null : parseFloat(newRow.effective_uplift_percent),
      };
      await api.post('/job-signoffs', payload);
      setShowAdd(false);
      setNewRow({ contractor_id: '', work_type: 'Concrete', effective_uplift_percent: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create signoff'); }
  };

  const sign = async (so, kind) => {
    const notes = window.prompt(`Notes for the ${kind === 'sign-supervisor' ? 'production supervisor' : 'quality inspector'} signoff (optional):`);
    try {
      await api.post(`/job-signoffs/${so._id}/${kind}`, { notes: notes || '' });
      toast.success('Signoff recorded');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not sign'); }
  };

  const updateUplift = async (so) => {
    const next = window.prompt('Effective uplift % for this job:', String(so.effective_uplift_percent ?? ''));
    if (next === null) return;
    try {
      await api.patch(`/job-signoffs/${so._id}`, { effective_uplift_percent: parseFloat(next) || 0 });
      toast.success('Uplift updated');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
  };

  return (
    <div data-testid="quality-signoff-card" className="bg-white border border-[#E2E2DF] rounded-xl p-5 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 uppercase tracking-[0.14em]">
          <ShieldCheck size={14} className="text-[#D35400]" /> Quality Signoff
        </h3>
        <button
          data-testid="quality-signoff-add-btn"
          onClick={() => setShowAdd(s => !s)}
          className="flex items-center gap-1 text-xs font-semibold text-[#D35400] hover:text-[#A04000]"
        ><Plus size={11} /> Add work signoff</button>
      </div>
      <p className="text-[11px] text-slate-500 -mt-1">
        Two signatures required per work type (Production Supervisor + Quality Inspector). Approval auto-increments the contractor's jobs counter.
      </p>

      {showAdd && (
        <div data-testid="quality-signoff-add-row" className="bg-slate-50 border border-[#E2E2DF] rounded-md p-3 grid grid-cols-1 md:grid-cols-4 gap-2">
          <select data-testid="signoff-new-contractor" value={newRow.contractor_id} onChange={e => setNewRow({ ...newRow, contractor_id: e.target.value })} className="px-2 py-1.5 border border-slate-200 rounded text-sm bg-white">
            <option value="">— Contractor —</option>
            {contractors.map(c => <option key={c._id} value={c._id}>{c.contractor_name || c.company_name}</option>)}
          </select>
          <select data-testid="signoff-new-worktype" value={newRow.work_type} onChange={e => setNewRow({ ...newRow, work_type: e.target.value })} className="px-2 py-1.5 border border-slate-200 rounded text-sm bg-white">
            {WORK_TYPES.map(w => <option key={w} value={w}>{w}</option>)}
          </select>
          <input data-testid="signoff-new-uplift" type="number" step="0.5" value={newRow.effective_uplift_percent} onChange={e => setNewRow({ ...newRow, effective_uplift_percent: e.target.value })} placeholder="Uplift % (optional)" className="px-2 py-1.5 border border-slate-200 rounded text-sm" />
          <button data-testid="signoff-new-save" onClick={addSignoff} className="text-sm font-bold bg-[#D35400] text-white rounded hover:bg-[#B84500]"><Check size={12} className="inline-block mr-1 -mt-0.5" /> Create</button>
        </div>
      )}

      {signoffs.length === 0 ? (
        <p className="text-xs text-slate-400 italic py-4 text-center">No signoffs raised yet.</p>
      ) : (
        <div className="space-y-2">
          {signoffs.map(so => {
            const sup = so.production_supervisor;
            const qa = so.quality_inspector;
            const cn = contractors.find(c => c._id === so.contractor_id)?.contractor_name || so.contractor_id.slice(-6);
            return (
              <div key={so._id} data-testid={`signoff-row-${so._id}`} className="border border-[#E2E2DF] rounded-lg p-3 grid grid-cols-1 md:grid-cols-6 gap-2 items-center">
                <div className="md:col-span-2">
                  <p className="text-sm font-semibold text-slate-800 flex items-center gap-1"><Hammer size={12} className="text-[#D35400]" /> {so.work_type}</p>
                  <p className="text-[11px] text-slate-500">{cn}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.1em] text-slate-500">Uplift</p>
                  <button data-testid={`signoff-uplift-${so._id}`} onClick={() => updateUplift(so)} className="text-sm font-bold text-emerald-700 hover:text-emerald-800 flex items-center gap-1">
                    {so.effective_uplift_percent === null || so.effective_uplift_percent === undefined ? '—' : `${Number(so.effective_uplift_percent).toFixed(1)}%`}
                    <Pencil size={10} />
                  </button>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.1em] text-slate-500">Supervisor</p>
                  {sup?.signed_at ? (
                    <p className="text-[11px] text-emerald-700 font-bold">✓ {sup.signed_by?.split('@')[0]}</p>
                  ) : (
                    <button data-testid={`signoff-sign-sup-${so._id}`} onClick={() => sign(so, 'sign-supervisor')} className="text-[11px] font-bold text-[#D35400] hover:text-[#A04000]">Sign as Sup</button>
                  )}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.1em] text-slate-500">Quality</p>
                  {qa?.signed_at ? (
                    <p className="text-[11px] text-emerald-700 font-bold">✓ {qa.signed_by?.split('@')[0]}</p>
                  ) : (
                    <button data-testid={`signoff-sign-qa-${so._id}`} onClick={() => sign(so, 'sign-quality')} className="text-[11px] font-bold text-[#D35400] hover:text-[#A04000]">Sign as QI</button>
                  )}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.1em] text-slate-500">Status</p>
                  <span className={`text-[10px] uppercase font-bold tracking-[0.1em] ${so.status === 'approved' ? 'text-emerald-700' : so.status === 'partial' ? 'text-amber-700' : so.status === 'rejected' ? 'text-red-700' : 'text-slate-500'}`}>
                    {so.status === 'approved' && <Check size={10} className="inline-block -mt-0.5" />} {so.status}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
